import React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import Label from '@zg-rentals/multibrand-ui/lib/Label';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import Section from '@rental-manager/components/Section';
import FaqComponent from '@common/components/FaqComponent';
import FaqConstants from '@rental-manager/constants/FaqConstants';
import { validateAllInputs } from '@rental-manager/pages/Onboarding/DueDatePage/validation';
import dueDates from '@rental-manager/pages/Onboarding/DueDatePage/dueDates.json';

type Props = {
  paymentDayOfTheMonth: number;
  onSubmit: ({ paymentDayOfTheMonth }) => void;
  editMode?: boolean;
  onCancel: () => void;
};

type State = {
  paymentDayOfTheMonth: number;
  showCaveat: boolean;
  error: string;
};

class NewTenantDueDate extends React.Component<Props, State> {
  static defaultProps = {
    paymentDayOfTheMonth: 1,
    onSubmit: () => {},
    editMode: false,
  };

  state = {
    paymentDayOfTheMonth: this.props.paymentDayOfTheMonth,
    showCaveat: false,
    error: '',
  };

  onChangeSelect = (e): void => {
    const selectedOption = e.currentTarget.options[e.currentTarget.selectedIndex];
    const paymentDayOfTheMonth = parseInt(selectedOption.getAttribute('value'), 10);
    const showCaveat = selectedOption.innerText?.endsWith('*');

    this.setState({
      showCaveat,
      paymentDayOfTheMonth,
      error: '',
    });
  };

  onSubmit = () => {
    const { paymentDayOfTheMonth } = this.state;

    if (!validateAllInputs(this)) {
      return { error: true };
    }

    this.props.onSubmit({ paymentDayOfTheMonth });
  };

  renderStartDateOptions = () => {
    const defaultOption = (
      <option key={-1} disabled value="">
        Select one
      </option>
    );
    const options = dueDates.map((date, index) => {
      if (date === null) {
        return (
          <option key={index} disabled>
            ──────
          </option>
        );
      }

      const [text, value] = date;
      return (
        <option key={index} value={value}>
          {text}
        </option>
      );
    });

    return [defaultOption, ...options];
  };

  render(): React.ReactNode {
    const { editMode } = this.props;
    const { showCaveat, paymentDayOfTheMonth, error } = this.state;
    const startDateOptions = this.renderStartDateOptions();
    return (
      <>
        <Section>
          <Text type="h3">When should your tenant send their rent each month?</Text>
        </Section>
        <Section>
          <Label className="flexDisplay" data-auto-test-id="payment-end-date-label">
            <span>Monthly payment date</span>
          </Label>
          <Select
            data-id="monthlyPaymentDate"
            value={paymentDayOfTheMonth}
            error={error}
            onChange={this.onChangeSelect}
          >
            {startDateOptions}
          </Select>
        </Section>
        {showCaveat && (
          <Section>
            <Text type="body-small">
              For any month with fewer than {paymentDayOfTheMonth} days, the last day of the month will be the default
              payment date.
            </Text>
          </Section>
        )}
        <Section>
          <Text type="body-small">
            Your renter is charged when they send payment. The funds reach your account a few days later.
          </Text>
        </Section>
        <Section>
          <SpacerRow>
            <FaqComponent.Landlord faqs={FaqConstants.ONBOARDING_V2.DUE_DATE_PAGE} />
          </SpacerRow>
        </Section>
        <Section>
          <Button type="primary" onClick={this.onSubmit} full>
            {editMode ? 'Update' : 'Continue'}
          </Button>
        </Section>
        {editMode && (
          <Section>
            <Button type="tertiary" full data-statekey="Cancel" onClick={this.props.onCancel}>
              Cancel
            </Button>
          </Section>
        )}
      </>
    );
  }
}

export default NewTenantDueDate;
